import {
  useInfiniteNotifications,
  useMarkAllRead,
} from "../_hooks/use-notifications";
import { useInfiniteScroll } from "@padi/lib/hooks/use-infinite-scroll";
import { NotificationItem } from "./notification-item";
import { Button } from "@padi/ui/button";
import { ScrollArea } from "@padi/ui/scroll-area";
import { Loader2, CheckCheck, BellOff } from "lucide-react";
import { Fragment } from "react";

interface NotificationListProps {
  onCheck?: () => void;
  onClose?: () => void;
}

export function NotificationList({ onCheck, onClose }: NotificationListProps) {
  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    isError,
    refetch,
  } = useInfiniteNotifications();

  const { sentinelRef } = useInfiniteScroll({
    onLoadMore: async () => {
      await fetchNextPage();
    },
    hasMore: !!hasNextPage,
    isLoading: isFetchingNextPage,
    rootMargin: "100px",
  });

  const isEmpty =
    !isLoading && (!data?.pages[0]?.items || data.pages[0].items.length === 0);

  return (
    <div className="flex flex-col h-full bg-background w-full">
      <div className="p-4 border-b flex items-center justify-between bg-background sticky top-0 z-10 shrink-0">
        <h3 className="font-semibold text-lg">Notifications</h3>
        <MarkAllReadButton />
      </div>

      <ScrollArea className="flex-1">
        <div className="flex flex-col min-h-0">
          {isLoading ? (
            <div className="flex flex-col items-center justify-center py-12 gap-2 text-muted-foreground">
              <Loader2 className="size-8 animate-spin" />
              <p className="text-sm">Loading notifications...</p>
            </div>
          ) : isError ? (
            <div className="flex flex-col items-center justify-center py-12 gap-2 text-muted-foreground">
              <BellOff className="size-8 opacity-50" />
              <p className="text-sm">Failed to load notifications</p>
              <Button variant="link" onClick={() => refetch()}>
                Retry
              </Button>
            </div>
          ) : isEmpty ? (
            <div className="flex flex-col items-center justify-center py-16 gap-3 text-muted-foreground text-center p-4">
              <div className="p-4 rounded-full bg-muted/50">
                <BellOff className="size-8 opacity-50" />
              </div>
              <div>
                <p className="font-medium text-foreground">
                  No notifications yet
                </p>
                <p className="text-sm">
                  We&apos;ll let you know when something important arrives.
                </p>
              </div>
            </div>
          ) : (
            <div className="flex flex-col divide-y">
              {data?.pages.map((page, i) => (
                <Fragment key={i}>
                  {page.items.map((notification) => (
                    <NotificationItem
                      key={notification.id}
                      notification={notification}
                      onClick={() => {
                        onCheck?.();
                        onClose?.();
                      }}
                    />
                  ))}
                </Fragment>
              ))}

              {/* Loading indicator for next page */}
              <div
                ref={sentinelRef}
                className="py-4 flex justify-center w-full min-h-[20px]"
              >
                {isFetchingNextPage && (
                  <Loader2 className="size-5 animate-spin text-muted-foreground" />
                )}
              </div>
            </div>
          )}
        </div>
      </ScrollArea>
    </div>
  );
}

function MarkAllReadButton() {
  const { mutate, isPending } = useMarkAllRead();
  return (
    <Button
      variant="ghost"
      size="sm"
      className="text-xs h-8 text-muted-foreground hover:text-primary gap-1"
      onClick={() => mutate()}
      disabled={isPending}
    >
      <CheckCheck className="size-3.5" />
      <span className="hidden sm:inline">Mark all read</span>
    </Button>
  );
}
